import {
  Box,
  Button,
  Divider,
  FormControl,
  ListItem,
  Menu,
  MenuItem,
  Radio,
  Select,
  Tooltip,
  Typography,
} from '@mui/material';
import { IOverrideFilterInfo } from '../../../../redux/Calculations/CurrentCalculationSessionInterface';
import { fontSize, palette } from '../../../..';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { TFilterType, TFilterTypeCamelCase } from '../../../../types/types';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline';
import {
  updateFilter,
  updateFilterPanel,
} from '../../../../redux/Calculations/CalculationsActions';
import {
  datePatternCollections,
  datePatternSearchConditionCollections,
} from '../../../FilterUtils/FilterPatternCollection';
import PickList from './PickList';
import RelativeFilter from './RelativeFilter';
import SearchCondition from './SearchCondition';
import { TFlowOverrideType } from '../../constants';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useViewport } from '@xyflow/react';

interface IOverrideItem {
  propKey: string;
  filterId: string;
  calculationUid: string | null;
  filterInfo?: IOverrideFilterInfo;
  overrideType: TFlowOverrideType;
  customized: boolean;
  displayName: string;
  dataType: string;
  tableId: string;
  fieldName: string;
  ensureNodeVisible: () => void;
}
function OverrideItem({
  propKey,
  filterId,
  calculationUid,
  filterInfo,
  overrideType,
  customized,
  displayName,
  dataType,
  fieldName,
  tableId,
  ensureNodeVisible,
}: IOverrideItem) {
  const { zoom } = useViewport();
  const filterTypes: {
    key: TFilterTypeCamelCase;
    value: TFilterType;
  }[] = [{ key: 'pickList', value: 'Pick List' }];
  if (dataType !== 'boolean') {
    filterTypes.push({
      key: 'searchCondition',
      value: 'Search Condition',
    });
  }
  if (dataType === 'date' || dataType === 'timestamp') {
    filterTypes.push({
      key: 'relativeFilter',
      value: 'Relative Filter',
    });
  }
  const timeGrains =
    filterInfo?.filterType === 'pickList'
      ? datePatternCollections
      : datePatternSearchConditionCollections;
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [valid, setValid] = useState<boolean>(true);
  const [showFilterOverrideInfo, setShowFilterOverrideInfo] = useState(
    overrideType === 'honorSome',
  );

  useEffect(() => {
    const isValid = () => {
      if (!filterInfo) return false;
      if (overrideType.includes('ignore')) return true;
      if (filterInfo.filterType === 'pickList') {
        return filterInfo.operator === 'in';
      } else if (filterInfo.filterType === 'searchCondition') {
        if (filterInfo.operator === 'blank') return true;
        if (filterInfo.userSelection.length === 0) return false;
        const val0 = filterInfo.userSelection[0];
        const val1 = filterInfo.userSelection[1];
        if (val0 === null || val0 === undefined || val0 === '') return false;
        if (filterInfo.operator === 'between') {
          if (val1 === null || val1 === undefined || val1 === '') return false;
          if (['date', 'timestamp'].includes(filterInfo.dataType)) {
            const date1 = new Date(filterInfo.userSelection[0]);
            const date2 = new Date(filterInfo.userSelection[1]);
            if (date1 && date2 && !isNaN(date1.getTime()) && !isNaN(date2.getTime())) {
              return date1 < date2;
            }
          } else {
            return (
              filterInfo.userSelection.length === 2 &&
              filterInfo.userSelection.every((item) =>
                ['integer', 'decimal'].includes(filterInfo.dataType) ? item !== null : !item,
              ) &&
              filterInfo.userSelection[1] > filterInfo.userSelection[0]
            );
          }
        } else {
          return filterInfo.userSelection.length === 1 && filterInfo.userSelection[0] !== null;
        }
      } else if (filterInfo.filterType === 'relativeFilter') {
        return true;
      }
      return false;
    };
    setValid(isValid());
  }, [filterInfo, overrideType]);
  const dispatch = useDispatch();

  const openFilterMenu = (e: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl((prev) => (prev === null ? e.currentTarget : null));
  };
  const handleMenuChange = (
    type: 'filter' | 'inc_exc' | 'timeGrain',
    value: TFilterType | boolean | string,
  ) => {
    if (!filterInfo) return;
    if (type === 'filter' && value === filterInfo.filterType) return;
    dispatch(
      updateFilterPanel(propKey, 'update', filterId, {
        ...filterInfo,
        ...(type === 'filter'
          ? {
            filterType: value as TFilterTypeCamelCase,
            userSelection: [],
            isAllSelected: false,
            operator:
              value === 'pickList'
                ? 'in'
                : value === 'relativeFilter'
                  ? 'between'
                  : filterInfo.dataType === 'text'
                    ? 'beginsWith'
                    : 'greaterThan',
          }
          : type === 'timeGrain'
            ? { timeGrain: value as string, userSelection: [] }
            : { shouldExclude: value as boolean }),
      }),
    );
  };
  const updateCustomization = (val: boolean) => {
    if (customized) {
      dispatch(updateFilterPanel(propKey, 'rmv', filterId));
    } else {
      dispatch(
        updateFilterPanel(propKey, 'add', filterId, {
          filterId: filterId,
          filterType: ['integer', 'decimal'].includes(dataType) ? 'searchCondition' : 'pickList',
          timeGrain: 'year',
          tableId,
          fieldName: fieldName,
          displayName: displayName,
          dataType,
          shouldExclude: false,
          operator: ['integer', 'decimal', 'float'].includes(dataType) ? 'greaterThan' : 'in',
          userSelection: [],

          isAllSelected: false,
          relativeCondition: null,
        }),
      );
    }
    dispatch(
      updateFilter(propKey, filterId, 'update', {
        tableId: tableId,
        fieldName: fieldName,
        displayName: displayName,
        dataType: dataType,
        timeGrain: 'year',
        customization: val,
        uid: filterId,
        isCalculatedField: !!calculationUid,
        calculatedFieldId: calculationUid ?? undefined,
      }),
    );
  };
  return (
    <>
      <ListItem
        key={filterId}
        sx={{
          border: `1px solid ${customized && !valid ? 'orange' : 'grey'}`,
          borderRadius: '5px',
          paddingInline: '0.3rem',
          display: 'flex',
          justifyContent: 'flex-start',
          alignItems: 'center',
          height: '1.5rem',
          width: '100%',
          gap: '0.2rem',
          boxSizing: 'border-box',
          marginBottom: '0.5rem',
        }}
      >
        <CloseRoundedIcon
          onClick={() => {
            dispatch(updateFilter(propKey, filterId, 'rmv'));
            dispatch(updateFilterPanel(propKey, 'rmv', filterId));
          }}
          sx={{
            fontSize: fontSize.medium,
            cursor: 'pointer',
            color: palette.primary.contrastText,
            '&:hover': {
              color: 'red',
              transform: 'scale(1.1)',
            },
          }}
        />
        <span
          title={displayName}
          style={{
            color: palette.primary.contrastText,
            display: 'flex',
            alignItems: 'center',
            textDecoration: overrideType.includes('ignore') ? 'line-through' : 'none',
          }}
        >
          {displayName}
          {overrideType === 'honorSome' && customized && !valid && (
            <Tooltip title="Field is not filled properly"
              slotProps={{
                tooltip: {
                  sx: {
                    fontSize: '12px',
                  },
                },
              }}
            >
              <WarningAmberIcon
                style={{
                  color: 'orange',
                  marginLeft: '5px',
                  opacity: '0.7',
                  flexShrink: 0,
                }}
                fontSize="small"
              />
            </Tooltip>
          )}
        </span>
        <Box
          sx={{
            marginLeft: 'auto',
            display: overrideType === 'honorSome' ? 'flex' : 'none',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Button
            type="button"
            className={`buttonCommon`}
            style={{ backgroundColor: 'transparent' }}
            title="Customize Filter"
            onClick={() => {
              updateCustomization(!customized);
              if (!customized) ensureNodeVisible()
            }}
            sx={{
              minWidth: 'fit-content',
              padding: 0,
            }}
          >
            <DriveFileRenameOutlineIcon
              fontSize="small"
              sx={{
                color: customized ? palette.primary.main : 'grey',
              }}
            />
          </Button>
          <Button
            type="button"
            className={`buttonCommon ${customized ? '' : 'disabled disabled-light'}`}
            style={{ backgroundColor: 'transparent' }}
            title="More Options"
            disabled={!customized}
            onClick={openFilterMenu}
            sx={{
              minWidth: 'fit-content',
              padding: 0,
            }}
          >
            <MoreVertIcon
              sx={{
                fontSize: fontSize.extraLarge,
                color: customized ? palette.primary.contrastText : 'grey',
                mr: 0,
              }}
            />
          </Button>
          {showFilterOverrideInfo ? (
            <KeyboardArrowDownIcon
              className={customized ? '' : 'disabled disabled-light'}
              onClick={() => customized && setShowFilterOverrideInfo(!showFilterOverrideInfo)}
              sx={{ cursor: 'pointer', color: palette.primary.contrastText }}
            />
          ) : (
            <KeyboardArrowRightIcon
              className={customized ? '' : 'disabled disabled-light'}
              onClick={() => {
                customized && setShowFilterOverrideInfo(!showFilterOverrideInfo)
                ensureNodeVisible()
              }}
              sx={{ cursor: 'pointer', color: palette.primary.contrastText }}
            />
          )}
        </Box>

        <Menu
          id="basic-menu"
          anchorEl={anchorEl}
          open={!!filterInfo && !!anchorEl}
          onClose={() => setAnchorEl(null)}
          MenuListProps={{
            'aria-labelledby': 'basic-button',
          }}
        >
          {filterInfo &&
            filterTypes.map((_filterType, index) => {
              return (
                <div
                  key={index}
                  style={{ display: 'flex' }}
                  onClick={() => {
                    // if (disabled) return;
                    if (_filterType.key === filterInfo.filterType) return;

                    handleMenuChange('filter', _filterType.key);
                    ensureNodeVisible()
                    setAnchorEl(null);
                  }}
                >
                  <Tooltip title={filterInfo.filterType === _filterType.key ? 'Selected' : null}
                    slotProps={{
                      tooltip: {
                        sx: {
                          fontSize: '12px',
                        },
                      },
                    }}
                  >
                    <Radio
                      // disabled={disabled}
                      checked={filterInfo.filterType === _filterType.key}
                      sx={{
                        '& .MuiSvgIcon-root': {
                          fontSize: '12px',
                          height: '12px',
                          color: '#af99db',
                        },
                        // fontSize: "0px",
                        alignSelf: 'center',
                        marginLeft: '5px',
                      }}
                    />
                  </Tooltip>
                  <MenuItem
                    key={index}
                    sx={{
                      flex: 1,
                      fontSize: '12px',
                      alignSelf: 'center',
                      padding: '2px 0px',
                      paddingRight: '1rem',
                    }}
                  >
                    {_filterType.value}
                  </MenuItem>
                </div>
              );
            })}
          <Divider
            sx={{
              margin: '5px 0px',
            }}
          />

          {/* Include */}
          {filterInfo && (
            <div
              style={{ display: 'flex' }}
              onClick={() => {
                // if (disabled) return;
                // dispatch(setShouldExclude(propKey, conditionFilterId, operandIndex, false));
                handleMenuChange('inc_exc', false);
                setAnchorEl(null);
              }}
            >
              <Tooltip title={!filterInfo.shouldExclude ? 'Selected' : null}
                slotProps={{
                  tooltip: {
                    sx: {
                      fontSize: '12px',
                    },
                  },
                }}
              >
                <Radio
                  checked={!filterInfo.shouldExclude}
                  // disabled={filterInfo.disabled}
                  sx={{
                    '& .MuiSvgIcon-root': {
                      fontSize: '12px',
                      height: '12px',
                      color: '#af99db',
                    },
                    alignSelf: 'center',
                    marginLeft: '5px',
                  }}
                />
              </Tooltip>
              <MenuItem
                sx={{
                  flex: 1,
                  fontSize: '12px',
                  alignSelf: 'center',
                  padding: '2px 0px',
                  paddingRight: '1rem',
                }}
              >
                Include
              </MenuItem>
            </div>
          )}

          {/* Exclude */}
          {filterInfo && (
            <div
              style={{ display: 'flex' }}
              onClick={() => {
                handleMenuChange('inc_exc', true);
                setAnchorEl(null);
              }}
            >
              <Tooltip title={filterInfo.shouldExclude ? 'Selected' : null}
                slotProps={{
                  tooltip: {
                    sx: {
                      fontSize: '12px',
                    },
                  },
                }}
              >
                <Radio
                  // disabled={disabled}
                  checked={filterInfo.shouldExclude}
                  sx={
                    filterInfo.shouldExclude
                      ? {
                        '& .MuiSvgIcon-root': {
                          fontSize: '12px',
                          height: '12px',
                          color: '#ffb74d',
                        },
                        alignSelf: 'center',
                        marginLeft: '5px',
                      }
                      : {
                        '& .MuiSvgIcon-root': {
                          fontSize: '12px',
                          height: '12px',
                          color: '#af99db',
                        },
                        alignSelf: 'center',
                        marginLeft: '5px',
                      }
                  }
                />
              </Tooltip>
              <MenuItem
                sx={{
                  flex: 1,
                  fontSize: '12px',
                  alignSelf: 'center',
                  padding: '2px 0px',
                  paddingRight: '1rem',
                }}
              >
                Exclude
              </MenuItem>
            </div>
          )}
        </Menu>
      </ListItem>
      <Box
        sx={{
          mb: 1,
        }}
      >
        {overrideType === 'honorSome' && customized && filterInfo && showFilterOverrideInfo && (
          <>
            {/* time grain */}
            {filterInfo.filterType !== 'relativeFilter' &&
              ['date', 'timestamp'].includes(filterInfo.dataType) && (
                <FormControl
                  fullWidth
                  size="small"
                  sx={{
                    marginBottom: '0.625rem',
                    '& .MuiOutlinedInput-root': {
                      '&.Mui-focused fieldset': {
                        borderColor: '#2bb9bb',
                      },
                      '&:hover fieldset': {
                        borderColor: '#2bb9bb',
                      },
                    },
                  }}
                >
                  <Select
                    sx={{
                      height: '1.5rem',
                      fontSize: fontSize.large,
                      textAlign: 'left',
                      '.MuiSelect-icon': {
                        fontSize: '18px',
                        // marginRight: '0.10rem',
                        color: palette.primary.contrastText,
                        // right: "3.5px",
                      },
                    }}
                    
                    IconComponent={KeyboardArrowDownIcon}
                    value={filterInfo.timeGrain}
                    variant="outlined"
                    onChange={(e) => handleMenuChange('timeGrain', e.target.value)}
                    MenuProps={{
                      slotProps: {
                        paper: {
                          sx: {
                            transform: `scale(${zoom}) !important`
                          }
                        }
                      },
                    }}
                  >
                    {timeGrains.map((item) => (
                      <MenuItem
                        key={item.key}
                        value={item.key}
                        selected={item.key === filterInfo.timeGrain}
                      >
                        <Typography
                          sx={{
                            width: 'auto',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            fontSize: '12px',
                            lineHeight: '20px',
                          }}
                        >
                          {item.value}
                        </Typography>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

            {filterInfo.filterType === 'pickList' && (
              <Box sx={{
                maxHeight: "170px", overflowY: "auto", scrollbarGutter: ' stable both-edges', '&::-webkit-scrollbar': {
                  width: '5px',
                }
              }}>
                <PickList propKey={propKey} filterInfo={filterInfo} calculationUid={calculationUid} ensureNodeVisible={ensureNodeVisible} />
              </Box>
            )}
            {filterInfo.filterType === 'searchCondition' && (
              <SearchCondition propKey={propKey} filterInfo={filterInfo} ensureNodeVisible={ensureNodeVisible}/>
            )}
            {filterInfo.filterType === 'relativeFilter' && (
              <RelativeFilter
                propKey={propKey}
                filterInfo={filterInfo}
                calculationUid={calculationUid}
              />
            )}
          </>
        )}
      </Box>
    </>
  );
}

export default OverrideItem;
